import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { GroupTreeDto } from '@/api/types';

interface CatalogState {
  groups: GroupTreeDto[];
  loading: boolean;
  error: string | null;
}

// Catalog of Groups (and their nested Projects). Add/archive actions mutate the server, then
// force-reload to re-pull the catalog. Errors from the actions are returned to the caller as
// `{ ok: false, error }` so the view can surface them inline rather than as toasts when that
// fits the flow better.
export const useCatalogStore = defineStore('catalog', {
  state: (): CatalogState => ({ groups: [], loading: false, error: null }),
  actions: {
    async load(force = false) {
      if (this.groups.length > 0 && !force) return;
      this.loading = true;
      this.error = null;
      try {
        this.groups = await adminApi.groups();
      } catch (e) {
        this.error = (e as Error).message;
      } finally {
        this.loading = false;
      }
    },

    async add(name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.addGroup({ name });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },

    async addProject(group: string, name: string): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.addProject(group, { name });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },

    async setGroupArchived(group: string, archived: boolean): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.setGroupArchived(group, { archived });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },

    async setProjectArchived(group: string, project: string, archived: boolean): Promise<{ ok: true } | { ok: false; error: string }> {
      try {
        await adminApi.setProjectArchived(group, project, { archived });
        await this.load(true);
        return { ok: true };
      } catch (e) {
        return { ok: false, error: (e as Error).message };
      }
    },
  },
});
